package com.evoapps.evoshift.domain.model

/**
 * SH-270: a business's own role (owners/{ownerId}/roles/{id}, written only by the saveRole
 * callable): a name and a level per feature.
 */
data class CustomRole(
    val id: String,
    val name: String,
    val levels: Map<Feature, AccessLevel>
)

/** A role a person can be given: built in ([builtIn]) or the business's own ([custom]). */
data class RoleChoice(val id: String, val builtIn: BuiltInRole?, val custom: CustomRole?) {
    /** Every feature's level. */
    val levels: Map<Feature, AccessLevel>
        get() = builtIn?.levels ?: Feature.entries.associateWith { custom?.levels?.get(it) ?: AccessLevel.NONE }

    companion object {
        fun of(role: BuiltInRole) = RoleChoice(role.id, role, null)
        fun of(role: CustomRole) = RoleChoice(role.id, null, role)
    }
}

/**
 * SH-270: the access rules the editor follows, the same as functions/roles.js setEngagementAccess
 * (which decides): roles are templates, a person's modifications are only those that differ from
 * the role, and nobody gives more than they have.
 */
object AccessRoles {
    /** The role's levels with [overrides] applied, every feature. */
    fun effective(roleLevels: Map<Feature, AccessLevel>, overrides: Map<Feature, AccessLevel>): Map<Feature, AccessLevel> =
        Feature.entries.associateWith { overrides[it] ?: roleLevels[it] ?: AccessLevel.NONE }

    /** The modifications that change something (an override equal to the role's level is dropped). */
    fun meaningful(roleLevels: Map<Feature, AccessLevel>, overrides: Map<Feature, AccessLevel>): Map<Feature, AccessLevel> =
        overrides.filter { (feature, level) -> (roleLevels[feature] ?: AccessLevel.NONE) != level }

    /**
     * The permission an engagement with [levels] keeps for older apps (functions/permissions.js
     * floorPermission): the highest built-in role whose every level they reach, never the owner's.
     */
    fun floorPermission(levels: Map<Feature, AccessLevel>): UserPermission =
        listOf(BuiltInRole.MANAGER_FINANCIAL, BuiltInRole.MANAGER, BuiltInRole.EMPLOYEE)
            .first { role -> Feature.entries.all { (levels[it] ?: AccessLevel.NONE).value >= role.levelOf(it).value } }
            .permission

    /**
     * The features where [levels] (or what the person has now, [current]) go beyond [mine], the
     * caller's own; empty when the caller may set them. The workplace's owner may set anything.
     */
    fun aboveOwn(
        levels: Map<Feature, AccessLevel>,
        current: Map<Feature, AccessLevel>,
        mine: Map<Feature, AccessLevel>,
        isOwner: Boolean
    ): Set<Feature> = if (isOwner) emptySet() else Feature.entries.filterTo(mutableSetOf()) {
        val own = (mine[it] ?: AccessLevel.NONE).value
        (levels[it] ?: AccessLevel.NONE).value > own || (current[it] ?: AccessLevel.NONE).value > own
    }

    /** The roles a person can be given: the built-in ones (not the owner's) and the business's own. */
    fun choices(custom: List<CustomRole>): List<RoleChoice> =
        listOf(BuiltInRole.MANAGER_FINANCIAL, BuiltInRole.MANAGER, BuiltInRole.EMPLOYEE).map(RoleChoice::of) +
            custom.sortedBy { it.name.lowercase() }.map(RoleChoice::of)

    /** The longest name of a custom role (functions/roles.js MAX_NAME). */
    const val MAX_NAME = 40
}

/** SH-270: why the server refused a role or an access change (functions/roles.js reasons). */
enum class AccessProblem {
    NOT_ALLOWED, OWN_ACCESS, ABOVE_OWN, OWNER_ACCESS, OWNER_ROLE, GROUP_GRANT, PLAN_ENDED,
    NAME_TAKEN, TOO_MANY_ROLES, ROLE_IN_USE, NOT_AN_OWNER, NOT_FOUND, MALFORMED, BUILT_IN
}

class AccessRefusedException(val problem: AccessProblem) : Exception("Access refused: $problem")
